import React from 'react';
import { CardBody } from '../shared/components/Card';
import { useUIState } from '../shared/contexts/UIStateProvider';
import { useCallState } from '../shared/contexts/CallProvider';
import { useRecording } from '../shared/contexts/RecordingProvider';
import { Modal } from '../../../../common/components/Modal/Modal';
import { Button, Stack } from '@mui/material';
import Loader from '../shared/components/Loader/Loader';
import { theme } from '../../../../common/styles/theme';
import { CallAppEvents } from '../CallApp/hocs/events';

export const LEAVE_MODAL = 'leave';

export const LeaveModal = () => {
	const { currentModals, closeModal } = useUIState();

	const { callObject, leave, saveLevel, ensureSaveLevelSynced } = useCallState() as any;

	const { stopRecording } = useRecording() as any;

	const [isStop, setStop] = React.useState(false);

	const onLeave = async () => {
		setStop(true);
		// Fire-and-forget: cloud recording finalizes server-side without awaiting.
		if (stopRecording) stopRecording();
		// End the session for everyone via server-side eject — works regardless
		// of guests' recording config or browser (unlike the recording->leave path).
		try {
			const participants = callObject?.participants?.() || {};
			const updates: { [sessionId: string]: { eject: true } } = {};
			Object.values(participants).forEach((p: any) => {
				if (p && !p.local && p.session_id) {
					updates[p.session_id] = { eject: true };
				}
			});
			if (Object.keys(updates).length) {
				callObject.updateParticipants(updates);
			}
		} catch (e) {
			console.error(e);
		}
		// Best-effort: let the separate /settings persist catch up too, so the DB row is right for
		// anything else reading it later — not load-bearing, saveLevel below covers this request.
		if (ensureSaveLevelSynced) await ensureSaveLevelSynced();
		// Still call /process even at saveLevel=nothing: its nothing-branch sweeps raw sources.
		// Passing saveLevel here (not relying on /settings' own async persist) closes the race.
		CallAppEvents.onProcess(saveLevel);
		// End for the owner too instead of waiting for the recording-stopped
		// event, which lags when a remote participant is slow to disconnect.
		try {
			if (leave) leave();
		} catch (e) {
			console.error(e);
		}
		// Close here since we no longer wait for the recording-stopped handler.
		closeModal(LEAVE_MODAL);
	};

	const isOpen = currentModals[LEAVE_MODAL];
	const isNothing = saveLevel === 'nothing';

	return (
		<Modal
			title={isNothing ? 'Finish the session' : 'Finish and prepare the materials'}
			open={!!isOpen}
			onClose={() => closeModal(LEAVE_MODAL)}
			PaperProps={{
				sx: {
					minWidth: `33vw !important`,
					maxWidth: `${theme.spacing(80)} !important`,
				},
			}}
		>
			<Stack>
				<CardBody>
					<p>
						Are you ready to finish the session?
					</p>
					<p>
						{isNothing
							? 'Save level is set to Nothing — nothing will be recorded or saved.'
							: 'The recording and transcription will be processed and saved.'
						}
					</p>
				</CardBody>

				<Button
					variant={'contained'}
					disabled={isStop}
					onClick={onLeave}
				>
					Finish
					{isStop && <Loader />}
				</Button>
			</Stack>
		</Modal>
	);
};
